import { DependencyType } from '@common/types/dependencyTypes';
import { ObjectLiteral } from '@prophecy/interfaces/generic';

import { Session } from '../../common/types/sessionTypes';
import { LinkType, ShareScopeType } from '../../graphqlTypes/enums';

export type ControlCenterResponse = {
  data: {
    key: string;
    url?: string;
  };
};

export type UsgaeMetricsResponse = {
  data: {
    aggregateMetrics: ObjectLiteral;
    requestID: string;
  };
};
export type AdminConfigType = { configType: string; value: object };
export enum ExecutionStatus {
  INITIALIZED = 'INITIALIZED', // this is an FE status doesnt exist in BE
  NOT_RUNNING = 'NOT_RUNNING',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  FAILED = 'FAILED',
  TIMEDOUT = 'TIMEDOUT',
  CANCELLED = 'CANCELLED',
  SKIPPED = 'SKIPPED',
  PENDING = 'PENDING',
  TERMINATING = 'TERMINATING',
  TERMINATED = 'TERMINATED',
  BLOCKED = 'BLOCKED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNKNOWN = 'UNKNOWN',
  UPSTREAM_SKIPPED = 'UPSTREAM_SKIPPED'
}

export type ExecutionLog = {
  message: string;
  level: string;
  trace?: string;
  errorCode?: string;
};

export enum AuthTypes {
  prophecymanaged = 'ProphecyManaged',
  ldap = 'ldap',
  saml = 'saml',
  azuread = 'azuread',
  google = 'google'
}

export enum ProviderType {
  google = 'google',
  okta = 'okta',
  azuread = 'azuread',
  others = 'others'
}

export type DatabrickUserPasswordPayload = {
  identifier: string;
  password: string;
  sendpromotionalemail: boolean;
  teamName?: string;
  firstName: string;
  lastName?: string;
  teamUID?: string;
  token?: string;
  email?: string;
  company?: string;
  teamNameUpdationAllowed?: string;
};

export enum ClusterActionType {
  restart = 'Restart',
  start = 'Start',
  terminate = 'Terminate',
  fixDependencies = 'FixDependencies'
}

export type LibraryOperation = 'Install' | 'Uninstall';

export type ClusterDatabricksOpsResponse = {
  libResults?: {
    status: 'Failure' | 'Success';
    message: string;
    installSummary: {
      total: number;
      successful: number;
      failed: number;
    };
    uninstallSummary: {
      total: number;
      successful: number;
      failed: number;
    };
    libraries: {
      name: string;
      operation: LibraryOperation;
      status: 'Failure' | 'Success';
      error: string;
    }[];
  };
  allowConnectAnyways: boolean;
  allowWebsocketCall: boolean;
  displayMessage: string;
};

export type ClusterDatabricksOpsPayload = {
  type: ClusterActionType;
  clusterId: string;
  libsToUninstall?: { dependencies: DependencyType[] };
  clusterData?: Session;
};

export enum KGProgressStatus {
  InProgress = 'IN_PROGRESS',
  Processing = 'PROCESSING',
  Error = 'ERROR',
  Success = 'SUCCESS',
  Pending = 'PENDING',
  Failed = 'FAILED',
  Idle = 'IDLE',
  Cancelled = 'CANCELLED'
}

export type KGConnectionStatus = {
  diagnostics?: string;
  total_databases: number;
  completed_databases: number;
  total_schemas: number;
  completed_schemas: number;
  total_tables: number;
  completed_tables: number;
  status: KGProgressStatus;
  completed_files: number;
  total_root_directories: number;
  completed_root_directories: number;
  last_completed_schema: string;
  last_completed_object: string;
};

export type KGCrawlStatusResponse = {
  session_id?: string;
  status?: KGProgressStatus;
  start_time?: number;
  last_run?: number;
  last_successful_run?: number;
  progress?: {
    total_connections: number;
    completed_connections: number;
    total_databases: number;
    completed_databases: number;
    total_schemas: number;
    completed_schemas: number;
    completed_files: number;
    connections: {
      [connectionName: string]: KGConnectionStatus;
    };
  };
};

// Flattened root status with progress metrics at top level (for hook return when no connectionName provided)
export type FlattenedRootStatus = {
  session_id?: string;
  status?: KGProgressStatus;
  start_time?: number;
  last_run?: number;
  last_successful_run?: number;
  // Flattened progress metrics
  total_connections?: number;
  completed_connections?: number;
  total_databases?: number;
  completed_databases?: number;
  total_schemas?: number;
  completed_schemas?: number;
  completed_files?: number;
  diagnostics?: { [connectionName: string]: string | undefined };
};

export type OrchestrationScheduledPipelineResponse = {
  pipelines?: {
    cron: string;
    projectId: string;
    pipelineName: string;
    fabricId: string;
    scheduleType: string;
    runs: {
      id: string;
      runStatus: string;
      createdAt: string;
      errorMessage?: string;
      sensorConfigDetails: string;
    }[];
  }[];
};

export enum SqlPipelineRunType {
  Scheduled = 'Scheduled',
  InteractivePipelineRun = 'InteractivePipelineRun',
  InteractiveAppRun = 'InteractiveAppRun',
  AppSchedule = 'AppSchedule',
  APIPipelineRun = 'APIPipelineRun'
}

export type OrchestrationPipelineRunResponse = {
  history?: {
    projectId: string;
    pipelineName: string;
    runStatus: string;
    startTime: string;
    runId: string;
    endTime?: string;
    errorMessage?: string;
    runType: SqlPipelineRunType;
    fabricId: string;
    duration_ns: number;
    appName?: string;
    instanceName?: string;
    cron: string;
    sensorConfigDetails: string;
    hasRuntimeLogs?: boolean;
  }[];
};

export type OrchestrationPipelineRunLogsResponse<UiPipelineGraph = unknown, Diagnostics = unknown> = {
  ID: number;
  CreatedAt: string | null;
  UpdatedAt: string | null;
  DeletedAt: string | null;
  run_id: string;
  run_request_params: {
    historicRunId?: string;
    pipelineState?: {
      currentPipelineGraph: UiPipelineGraph;
      diagnostics: Diagnostics;
    };
  } & Record<string, unknown>;
  messages: {
    messageNumber: number;
    time: string;
    message: {
      method: string;
      params: {
        content: string;
        progress?: {
          process_id: string;
          start_time: number;
          task_state: number;
          end_time?: number;
          exception?: string;
        };
      };
    };
  }[];
};

export type ReplayDetails = {
  replayDetails: {
    uid: string;
    name: string;
    description: string;
    replayId: string;
    sharedUserUID: string;
    projectUID: string;
    fabricUID: string;
    pipelineName: string;
    appNames: string[];
    replayJson: string;
    shareLink: string;
    createdAt: string;
  };
  jwtToken?: string;
  hasDirectAccess: boolean;
  loggedInUser?: string;
};
export type ShareDetails = {
  shareDetails: {
    uid: string;
    shareId: string;
    sharedUserUID: string;
    projectUID: string;
    fabricUID: string;
    shareScope: ShareScopeType;
    entityURI?: string;
    linkType?: LinkType;
  };
  jwtToken?: string;
  hasDirectAccess: boolean;
  loggedInUser?: string;
};
export type BotCheckResponse = {
  isBot: boolean;
  score: number;
};

export type FeatureSchedulerPayload = {
  featureID: string;
  action: 'enabled' | 'disabled';
  selectedOption?: string;
}[];
